import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import type { CreditBalance } from "@/lib/credit-types";
import { calculateCredits, type CreditEstimateInput, type CreditEstimateBreakdown } from "@/lib/credit-calculator";
import { config } from "@/lib/config";
import { SUBSCRIPTION_TIERS, type SubscriptionTier } from "@/lib/pricing";
import { capabilityDecision, exportCapabilityDecision, runCapabilityDecision, type Capability, type EntitlementDecision } from "@/lib/entitlements";
import type { BackendExportFormat } from "@/lib/backend-export";
import type { ModuleType } from "@/lib/types";

export { SUBSCRIPTION_TIERS };
export type { SubscriptionTier };

interface CreditUsageState {
  balance: CreditBalance | null;
  loading: boolean;
}

interface ResolvedCapability {
  allowed: boolean;
  reason?: string;
  source?: string;
  finalValue?: any;
}

interface ResolvedControlsState {
  loading: boolean;
  effectiveTier: SubscriptionTier | null;
  capabilities: Partial<Record<Capability, ResolvedCapability>>;
  featureFlags: Record<string, { enabled: boolean; source?: string; reason?: string | null }>;
  risk: { blocked: boolean; restricted: boolean; reason: string | null };
  overrides: any[];
  lastResolvedAt: string | null;
}

interface SubscriptionState {
  subscribed: boolean;
  tier: SubscriptionTier;
  productId: string | null;
  subscriptionEnd: string | null;
  isTrial: boolean;
  trialEndsAt: string | null;
  trialEligible: boolean;
  loading: boolean;
  error: string | null;
  credits: CreditUsageState;
  controls: ResolvedControlsState;
}

interface SubscriptionContextType extends SubscriptionState {
  localDevTier: SubscriptionTier | null;
  setLocalDevTier: (tier: SubscriptionTier) => Promise<void>;
  refreshSubscription: () => Promise<void>;
  refreshCredits: () => Promise<void>;
  refreshEntitlements: () => Promise<void>;
  createCheckout: (tier: SubscriptionTier, billingPeriod?: "monthly" | "annual", trial?: boolean) => Promise<string | null>;
  openCustomerPortal: () => Promise<void>;
  // Credit-based methods
  canAfford: (estimate: CreditEstimateBreakdown) => boolean;
  estimateCredits: (input: CreditEstimateInput) => CreditEstimateBreakdown;
  consumeCredits: (input: CreditEstimateInput & { runId?: string }) => Promise<{ success: boolean; message?: string }>;
  purchaseTopup: (priceId: string) => Promise<string | null>;
  capabilityDecisionFor: (capability: Capability) => EntitlementDecision;
  runDecisionFor: (moduleType: ModuleType) => EntitlementDecision;
  exportDecisionFor: (format: BackendExportFormat) => EntitlementDecision;
  featureFlagEnabled: (flagKey: string) => boolean;

  // Legacy methods for backward compatibility
  canUseReview: () => boolean;
  incrementUsage: () => Promise<{ success: boolean; message?: string }>;
  usage: {
    creditsUsed: number;
    creditsLimit: number;
    loading: boolean;
  };
}

const LOCAL_DEV_TIER_STORAGE_KEY = "reqqy.localDev.subscriptionTier";

const normalizeSubscriptionTier = (value: any): SubscriptionTier | null => {
  if (typeof value !== "string") return null;
  return value in SUBSCRIPTION_TIERS ? (value as SubscriptionTier) : null;
};

const readLocalDevTier = (): SubscriptionTier | null => {
  if (!config.isLocalhost || typeof window === "undefined") return null;
  return normalizeSubscriptionTier(window.localStorage.getItem(LOCAL_DEV_TIER_STORAGE_KEY));
};

const writeLocalDevTier = (tier: SubscriptionTier) => {
  if (!config.isLocalhost || typeof window === "undefined") return;
  window.localStorage.setItem(LOCAL_DEV_TIER_STORAGE_KEY, tier);
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

const getPlanCreditBalance = (tier: SubscriptionTier): CreditBalance => {
  const monthlyCredits = SUBSCRIPTION_TIERS[tier]?.monthlyCredits ?? SUBSCRIPTION_TIERS.free.monthlyCredits;
  const monthlyResetAt = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString();
  return {
    monthlyCredits,
    monthlyUsed: 0,
    monthlyRemaining: monthlyCredits,
    topupCredits: 0,
    promoCredits: 0,
    totalRemaining: monthlyCredits,
    monthlyResetAt,
  };
};

const getDefaultCreditBalance = (): CreditBalance => getPlanCreditBalance("free");

const getDefaultState = (): SubscriptionState => ({
  subscribed: false,
  tier: "free",
  productId: null,
  subscriptionEnd: null,
  isTrial: false,
  trialEndsAt: null,
  trialEligible: true,
  loading: false,
  error: null,
  credits: {
    balance: getDefaultCreditBalance(),
    loading: false,
  },
  controls: {
    loading: false,
    effectiveTier: null,
    capabilities: {},
    featureFlags: {},
    risk: { blocked: false, restricted: false, reason: null },
    overrides: [],
    lastResolvedAt: null,
  },
});

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [state, setState] = useState<SubscriptionState>(getDefaultState);
  const [localDevTier, setLocalDevTierState] = useState<SubscriptionTier | null>(() => readLocalDevTier());

  const refreshCredits = useCallback(async () => {
    if (!session) return;

    if (config.isLocalhost && localDevTier) {
      setState(prev => ({
        ...prev,
        credits: {
          balance: getPlanCreditBalance(localDevTier),
          loading: false,
        },
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      credits: { ...prev.credits, loading: true },
    }));

    try {
      const { data, error } = await supabase.functions.invoke("check-usage");
      
      if (error) throw error;
      
      if (data.credits) {
        setState(prev => ({
          ...prev,
          credits: {
            balance: {
              monthlyCredits: data.credits.monthly_credits,
              monthlyUsed: data.credits.monthly_used,
              monthlyRemaining: data.credits.monthly_remaining,
              topupCredits: data.credits.topup_credits,
              promoCredits: data.credits.promo_credits ?? 0,
              totalRemaining: data.credits.total_remaining,
              monthlyResetAt: data.credits.monthly_reset_at,
            },
            loading: false,
          },
        }));
      }
    } catch (error) {
      console.error("Error checking credits:", error);
      setState(prev => ({
        ...prev,
        credits: { ...prev.credits, loading: false },
      }));
    }
  }, [session, localDevTier]);

  const refreshSubscription = useCallback(async () => {
    if (!session) {
      setState(getDefaultState());
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const { data, error } = await supabase.functions.invoke("check-subscription", {
        body: config.isLocalhost && localDevTier ? { localDevTier } : undefined,
      });
      
      if (error) throw error;
      
      const tier = (data.tier as SubscriptionTier) || "free";
      
      setState(prev => ({
        ...prev,
        subscribed: data.subscribed,
        tier,
        productId: data.product_id,
        subscriptionEnd: data.subscription_end,
        isTrial: data.is_trial ?? false,
        trialEndsAt: data.trial_ends_at ?? null,
        trialEligible: data.trial_eligible ?? true,
        loading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Error checking subscription:", error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to check subscription",
      }));
    }
  }, [session, localDevTier]);


  const refreshEntitlements = useCallback(async () => {
    if (!session) return;

    setState(prev => ({
      ...prev,
      controls: { ...prev.controls, loading: true },
    }));

    try {
      const { data, error } = await supabase.functions.invoke("resolve-entitlements", {
        body: {
          currentTier: localDevTier ?? state.tier,
          localDevTier: config.isLocalhost ? localDevTier : null,
        },
      });

      if (error) throw error;

      const effectiveTier = normalizeSubscriptionTier(data?.effectiveTier) ?? state.tier;
      const usage = data?.usage;

      setState(prev => ({
        ...prev,
        tier: effectiveTier,
        subscribed: effectiveTier !== "free",
        credits: usage ? {
          balance: {
            monthlyCredits: Number(usage.monthlyCredits ?? prev.credits.balance?.monthlyCredits ?? 0),
            monthlyUsed: Number(usage.monthlyUsed ?? prev.credits.balance?.monthlyUsed ?? 0),
            monthlyRemaining: Number(usage.monthlyRemaining ?? prev.credits.balance?.monthlyRemaining ?? 0),
            topupCredits: Number(usage.topupCredits ?? prev.credits.balance?.topupCredits ?? 0),
            promoCredits: prev.credits.balance?.promoCredits ?? 0,
            totalRemaining: Number(usage.totalRemaining ?? prev.credits.balance?.totalRemaining ?? 0),
            monthlyResetAt: prev.credits.balance?.monthlyResetAt ?? new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString(),
          },
          loading: false,
        } : prev.credits,
        controls: {
          loading: false,
          effectiveTier,
          capabilities: data?.capabilities ?? {},
          featureFlags: data?.featureFlags ?? {},
          risk: data?.risk ?? { blocked: false, restricted: false, reason: null },
          overrides: Array.isArray(data?.overrides) ? data.overrides : [],
          lastResolvedAt: new Date().toISOString(),
        },
      }));
    } catch (error) {
      console.warn("Entitlement resolver unavailable; falling back to plan entitlements", error);
      setState(prev => ({
        ...prev,
        controls: { ...prev.controls, loading: false },
      }));
    }
  }, [session, localDevTier, state.tier]);

  const setLocalDevTier = useCallback(async (tier: SubscriptionTier) => {
    if (!config.isLocalhost) return;

    writeLocalDevTier(tier);
    setLocalDevTierState(tier);

    setState(prev => ({
      ...prev,
      subscribed: tier !== "free",
      tier,
      productId: tier === "free" ? null : `local_dev_${tier}`,
      subscriptionEnd: tier === "free" ? null : new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
      isTrial: false,
      trialEndsAt: null,
      trialEligible: false,
      loading: false,
      error: null,
      credits: {
        balance: getPlanCreditBalance(tier),
        loading: false,
      },
      controls: {
        ...prev.controls,
        effectiveTier: tier,
        capabilities: {},
        featureFlags: {},
        risk: { blocked: false, restricted: false, reason: null },
      },
    }));
  }, []);

  const createCheckout = useCallback(async (tier: SubscriptionTier, billingPeriod: "monthly" | "annual" = "monthly", trial: boolean = false): Promise<string | null> => {
    if (!session) return null;
    
    const tierData = SUBSCRIPTION_TIERS[tier];
    const priceId = billingPeriod === "annual" ? tierData.price_id_annual : tierData.price_id_monthly;
    if (!priceId) {
      console.error("No price ID for tier:", tier, billingPeriod);
      return null;
    }

    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId, trial },
      });
      
      if (error) throw error;
      return data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      return null;
    }
  }, [session]);

  const openCustomerPortal = useCallback(async () => {
    if (!session) return;

    try {
      const { data, error } = await supabase.functions.invoke("customer-portal");
      
      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
    }
  }, [session]);

  // Credit-based methods
  const estimateCredits = useCallback((input: CreditEstimateInput): CreditEstimateBreakdown => {
    return calculateCredits(input);
  }, []);

  const canAfford = useCallback((estimate: CreditEstimateBreakdown): boolean => {
    const balance = state.credits.balance;
    if (!balance) return false;
    return balance.totalRemaining >= estimate.totalCredits;
  }, [state.credits.balance]);

  const consumeCredits = useCallback(async (
    input: CreditEstimateInput & { runId?: string }
  ): Promise<{ success: boolean; message?: string }> => {
    if (!session) return { success: false, message: "Not authenticated" };

    try {
      const { data, error } = await supabase.functions.invoke("increment-usage", {
        body: {
          mode: input.mode,
          depth: input.depth,
          inputLength: input.inputLength,
          personaCount: input.personaCount,
          runId: input.runId,
        },
      });
      
      if (error) throw error;
      
      if (data.success) {
        // Update local state with new balance
        setState(prev => ({
          ...prev,
          credits: {
            ...prev.credits,
            balance: prev.credits.balance ? {
              ...prev.credits.balance,
              monthlyRemaining: data.monthly_remaining,
              topupCredits: data.topup_remaining,
              totalRemaining: data.credits_remaining,
              monthlyUsed: prev.credits.balance.monthlyCredits - data.monthly_remaining,
            } : null,
          },
        }));
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error("Error consuming credits:", error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : "Failed to consume credits" 
      };
    }
  }, [session]);

  const purchaseTopup = useCallback(async (priceId: string): Promise<string | null> => {
    if (!session) return null;

    try {
      const { data, error } = await supabase.functions.invoke("purchase-topup", {
        body: { priceId },
      });
      
      if (error) throw error;
      return data.url;
    } catch (error) {
      console.error("Error creating topup checkout:", error);
      return null;
    }
  }, [session]);


  const capabilityDecisionFor = useCallback((capability: Capability): EntitlementDecision => {
    const resolved = state.controls.capabilities[capability];
    if (resolved) {
      return {
        allowed: Boolean(resolved.allowed),
        reason: resolved.allowed ? undefined : resolved.reason || "Blocked by admin control",
        upgradeLabel: resolved.source === "plan" ? undefined : "Admin controlled",
      };
    }
    return capabilityDecision(state.tier, capability);
  }, [state.controls.capabilities, state.tier]);

  const runDecisionFor = useCallback((moduleType: ModuleType): EntitlementDecision => {
    const capability = moduleType === "generation" ? "generation" : moduleType === "evaluation" ? "evaluation" : moduleType === "refinement" ? "refinement" : "prototype";
    const resolved = capabilityDecisionFor(capability);
    return resolved.allowed ? resolved : { ...resolved, reason: resolved.reason || runCapabilityDecision(state.tier, moduleType).reason };
  }, [capabilityDecisionFor, state.tier]);

  const exportDecisionFor = useCallback((format: BackendExportFormat): EntitlementDecision => {
    const capability =
      format === "markdown" ? "export_markdown" :
      format === "csv" ? "export_csv" :
      format === "json" ? "export_json" :
      format === "jira_csv" ? "export_jira_csv" :
      format === "linear_csv" ? "export_linear_csv" :
      "export_notion";
    const resolved = capabilityDecisionFor(capability);
    return resolved.allowed ? resolved : { ...resolved, reason: resolved.reason || exportCapabilityDecision(state.tier, format).reason };
  }, [capabilityDecisionFor, state.tier]);

  const featureFlagEnabled = useCallback((flagKey: string): boolean => {
    const flag = state.controls.featureFlags[flagKey];
    return Boolean(flag?.enabled);
  }, [state.controls.featureFlags]);

  // Legacy methods for backward compatibility
  const canUseReview = useCallback(() => {
    const balance = state.credits.balance;
    if (!balance) return false;
    return balance.totalRemaining >= 1;
  }, [state.credits.balance]);

  const incrementUsage = useCallback(async (): Promise<{ success: boolean; message?: string }> => {
    // Use default values for legacy calls
    return consumeCredits({
      mode: "evaluation",
      depth: "quick",
      inputLength: 500,
      personaCount: 1,
    });
  }, [consumeCredits]);

  // Usage object mapping credits to plan
  const usage = {
    creditsUsed: state.credits.balance?.monthlyUsed ?? 0,
    creditsLimit: state.credits.balance?.monthlyCredits ?? SUBSCRIPTION_TIERS[state.tier].monthlyCredits,
    loading: state.credits.loading,
  };

  // Refresh subscription on auth change
  useEffect(() => {
    if (user) {
      refreshSubscription();
    } else {
      setState(getDefaultState());
    }
  }, [user, refreshSubscription]);

  // Refresh credits after subscription is loaded
  useEffect(() => {
    if (user && !state.loading && state.tier) {
      refreshCredits();
    }
  }, [user, state.loading, state.tier, refreshCredits]);


  useEffect(() => {
    if (!user || state.loading) return;
    refreshEntitlements();
  }, [user, state.loading, state.tier, localDevTier, refreshEntitlements]);

  // Realtime subscription on credit_balances for instant meter updates
  useEffect(() => {
    if (!user) return;
    if (config.isLocalhost && localDevTier) return;

    const channel = supabase
      .channel("credit-balance-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "credit_balances",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as Record<string, any> | undefined;
          if (row) {
            const monthlyCredits = (row.monthly_credits as number) ?? 0;
            const monthlyUsed = (row.monthly_used as number) ?? 0;
            const topupCredits = (row.topup_credits as number) ?? 0;
            const monthlyRemaining = monthlyCredits - monthlyUsed;

            setState((prev) => ({
              ...prev,
              credits: {
                ...prev.credits,
                balance: {
                  monthlyCredits,
                  monthlyUsed,
                  monthlyRemaining,
                  topupCredits,
                  promoCredits: prev.credits.balance?.promoCredits ?? 0,
                  totalRemaining: monthlyRemaining + topupCredits,
                  monthlyResetAt: (row.monthly_reset_at as string) ?? prev.credits.balance?.monthlyResetAt ?? "",
                },
              },
            }));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, localDevTier]);

  useEffect(() => {
    if (!config.isLocalhost || typeof window === "undefined") return;

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== LOCAL_DEV_TIER_STORAGE_KEY) return;
      const nextTier = normalizeSubscriptionTier(event.newValue);
      setLocalDevTierState(nextTier);
      if (nextTier) {
        setState(prev => ({
          ...prev,
          subscribed: nextTier !== "free",
          tier: nextTier,
          productId: nextTier === "free" ? null : `local_dev_${nextTier}`,
          subscriptionEnd: nextTier === "free" ? null : new Date(Date.now() + 1000 * 60 * 60 * 24 * 30).toISOString(),
          isTrial: false,
          trialEndsAt: null,
          trialEligible: false,
          credits: {
            balance: getPlanCreditBalance(nextTier),
            loading: false,
          },
        }));
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Auto-refresh subscription every 5 minutes (credits handled by realtime now)
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      refreshSubscription();
    }, 300000);
    return () => clearInterval(interval);
  }, [user, refreshSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        ...state,
        localDevTier,
        setLocalDevTier,
        refreshSubscription,
        refreshCredits,
        refreshEntitlements,
        createCheckout,
        openCustomerPortal,
        canAfford,
        estimateCredits,
        consumeCredits,
        purchaseTopup,
        capabilityDecisionFor,
        runDecisionFor,
        exportDecisionFor,
        featureFlagEnabled,
        // Legacy
        canUseReview,
        incrementUsage,
        usage,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
}
